import React, { useState, useEffect } from "react";
import Nuova from "../imgs/Nuova orchestra.jpeg";
import Orchestra from "../imgs/Galleria/Immagini nuova orchestra (1).webp";
import Orchestra2 from "../imgs/Galleria/Immagini nuova orchestra (2).webp";
import { FaMusic, FaUserTie, FaSearchPlus } from "react-icons/fa";
import { GiMusicalNotes } from "react-icons/gi";

/* ------------------------------------------------------------------ */
/*  Dati                                                               */
/* ------------------------------------------------------------------ */
const sezioni = [
  [
    "Violini",
    "Francesco Di Giacinto, Teresa Lombardi, Rossella Serino, Mariangela Franco Montelli, Simonetta Sargenti, Anna Lombardi",
  ],
  ["Viole", "Svitlana Fedorova, Sabrina Semeraro, Rosaria Mastrosimone"],
  ["Violoncelli", "Luca Russo Rossi, Annalisa Di Leo, Elisa Missaglia"],
  ["Contrabbassi", "Vito Tagliente, Sergio Fiacconi"],
  ["Flauti", "Chiara Brunelli, Chiara Pasqualini, Lidia Savoia"],
  ["Oboi", "Alessandro Nardi, Imma Mincolla"],
  ["Clarinetti", "Francesco Silipigni, Alessio Quaglia, Mario Montani"],
  ["Fagotto", "Domenico Serrecchia"],
  ["Corni", "Ana Abramovic, Federico Ceccarelli"],
  ["Trombe", "Raffaele Quaranta, Desario Ruggero"],
  ["Tromboni", "Ilario Roccisano, Antonio Rizzuto"],
  ["Timpani", "Roberto Manclossi"],
  ["Percussioni", "Paolo Mogni, Papanikolaou Stavros"],
  ["Arpa", "Maria Pistone"],
].map(([nome, elenco]) => ({ nome, musicisti: elenco.split(", ") }));

// Il totale si aggiorna da solo se aggiungi o togli nomi dall'elenco
const totaleMusicisti = sezioni.reduce((tot, s) => tot + s.musicisti.length, 0);

/* ------------------------------------------------------------------ */
/*  PhotoCard: foto cliccabile con lente per ingrandire                */
/* ------------------------------------------------------------------ */
const PhotoCard = ({ src, alt, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    aria-label={`Ingrandisci: ${alt}`}
    className="relative w-full h-64 md:h-80 rounded-2xl overflow-hidden shadow-xl bg-amber-100 cursor-pointer group focus:outline-none focus-visible:ring-4 focus-visible:ring-red-400"
  >
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
    />
    <span className="absolute bottom-3 right-3 flex items-center justify-center w-10 h-10 rounded-full bg-black/70 text-white text-sm group-hover:bg-red-600 transition-colors duration-300">
      <FaSearchPlus />
    </span>
  </button>
);

/* ------------------------------------------------------------------ */
/*  Pagina                                                             */
/* ------------------------------------------------------------------ */
const NuovaOrchestra = () => {
  const [fullScreenImage, setFullScreenImage] = useState(null);

  const handleImageClick = (img) => setFullScreenImage(img);
  const closeImage = () => setFullScreenImage(null);

  // Chiude l'immagine a schermo intero con il tasto Esc
  useEffect(() => {
    if (!fullScreenImage) return;
    const onKey = (e) => {
      if (e.key === "Escape") setFullScreenImage(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [fullScreenImage]);

  return (
    <div className="px-4 md:px-8 pb-16 flex flex-col items-center w-full min-h-screen bg-gradient-to-r from-white to-amber-50 text-gray-800 overflow-hidden">
      <GiMusicalNotes className="fixed text-amber-200/40 text-[180px] rotate-12 -top-5 -right-5 z-0" />

      {/* --- INTESTAZIONE --- */}
      <header className="relative z-10 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10 w-full md:w-3/4 lg:w-2/3 mt-10 mb-10 text-center md:text-left">
        <img
          src={Nuova}
          alt="Logo Nuova Orchestra"
          className="w-28 md:w-40 rounded-2xl shadow-lg object-contain"
        />
        <div>
          <h1 className="text-red-600 font-extrabold text-3xl md:text-5xl tracking-wide">
            Nuova Orchestra di Milano
          </h1>
          <p className="mt-4 inline-flex items-center gap-3 text-xl text-gray-800">
            <FaUserTie className="text-red-600 text-2xl" />
            <span>
              Direttore:{" "}
              <a
                href="https://www.giuseppedinardo.it/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-600 font-semibold hover:underline"
              >
                Giuseppe Dinardo
              </a>
            </span>
          </p>
        </div>
      </header>

      {/* --- FOTO --- */}
      <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 gap-6 w-full md:w-3/4 lg:w-2/3 mb-14">
        <PhotoCard
          src={Orchestra}
          alt="Foto della Nuova Orchestra di Milano"
          onOpen={() => handleImageClick(Orchestra)}
        />
        <PhotoCard
          src={Orchestra2}
          alt="Un'altra foto della Nuova Orchestra di Milano"
          onOpen={() => handleImageClick(Orchestra2)}
        />
      </div>

      {/* --- IL PROGETTO --- */}
      <section className="relative z-10 w-full md:w-3/4 lg:w-2/3 max-w-3xl mb-16 text-gray-700 leading-relaxed">
        <h2 className="text-3xl font-bold text-red-600 mb-6">Il progetto</h2>
        <div className="space-y-6 text-lg">
          <p className="text-xl text-gray-800">
            In un periodo storico in cui molte orchestre hanno dovuto chiudere i
            battenti, <strong>MilARTE</strong> ha deciso di affrontare con
            determinazione questa sfida culturale. L’orchestra si ispira alle
            prestigiose formazioni internazionali, diventando un luogo di
            aggregazione che celebra la bellezza del <em>“Fare Musica”</em> e
            promuove la sensibilità artistica e sociale.
          </p>
          <p>
            La <strong>Nuova Orchestra di Milano</strong> accoglie musicisti
            professionisti e semiprofessionisti che vogliono esprimere il
            proprio talento all’interno di un contesto orchestrale ricco di
            creatività e collaborazione. Un ambiente in cui le passioni
            musicali si uniscono alla crescita personale e artistica.
          </p>
          <p>
            Questo progetto, di grande valore culturale ed educativo, allinea
            Milano a un modello di vita musicale basato su socialità,
            corresponsabilità e innovazione, aprendosi a collaborazioni con
            altre città, università, teatri e scuole di musica.
          </p>
        </div>
      </section>

      {/* --- MEMBRI --- */}
      <section className="relative z-10 w-full md:w-4/5 lg:w-2/3 bg-white/70 backdrop-blur-md rounded-3xl shadow-md border border-amber-100 p-8 md:p-12">
        <div className="flex flex-col items-center text-center mb-10">
          <FaMusic className="text-red-600 text-2xl mb-2" />
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
            Membri dell’Orchestra
          </h2>
          <p className="mt-1 text-gray-600">
            {totaleMusicisti} musicisti in {sezioni.length} sezioni
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
          {sezioni.map((sezione) => (
            <div
              key={sezione.nome}
              className="border-l-4 border-red-600 pl-4"
            >
              <h3 className="font-extrabold text-red-600 text-lg">
                {sezione.nome}
              </h3>
              <ul className="mt-1 text-gray-700 space-y-0.5">
                {sezione.musicisti.map((nome) => (
                  <li key={nome}>{nome}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* --- IMMAGINE A SCHERMO INTERO --- */}
      {fullScreenImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closeImage}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={fullScreenImage}
            alt="Immagine ingrandita"
            className="max-w-[90vw] max-h-[90vh] rounded-2xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
};

export default NuovaOrchestra;